/**
 * CrewRelationshipManager - Singleton for managing crew relationships
 * 
 * INTEGRATION GUIDE:
 * 
 * 1. Mission Impact:
 *    After mission completion, call:
 *    CrewRelationshipManager.applyMissionImpact(crewIds, success)
 * 
 * 2. Personality Conflicts:
 *    When crew interact, check:
 *    CrewRelationshipManager.checkPersonalityConflict(crew1, crew2)
 * 
 * 3. Time Decay:
 *    In game loop (every 5-10 minutes), call:
 *    CrewRelationshipManager.applyTimeDecay(crewIds)
 * 
 * 4. Random Events:
 *    Periodically (every 2-5 minutes), call:
 *    CrewRelationshipManager.triggerRandomEvent(crew)
 * 
 * 5. Combat Bonus:
 *    In combat damage calculation:
 *    damage *= CrewRelationshipManager.getCombatBonus(crewIds)
 * 
 * 6. Efficiency Bonus:
 *    In resource consumption:
 *    consumption /= CrewRelationshipManager.getEfficiencyBonus(crewIds)
 */

import EventBus, { EVENT_TYPES } from '../system/EventBus';
import CrewManager from './CrewManager';

import GameConfig from '../../config/GameConfig';

const config = GameConfig.crewRelationships;

const RELATIONSHIP_STATUS = {
  HOSTILE: 'HOSTILE',
  DISTANT: 'DISTANT', 
  NEUTRAL: 'NEUTRAL',
  FRIENDLY: 'FRIENDLY',
  CLOSE: 'CLOSE'
};

const STORAGE_KEY = 'starship_crew_relationships';

class CrewRelationshipManager {
  constructor() {
    if (CrewRelationshipManager.instance) {
      return CrewRelationshipManager.instance;
    }
    
    this.relationships = new Map();
    this.load();
    CrewRelationshipManager.instance = this;
  }

  getRelationshipKey(crewId1, crewId2) {
    return [crewId1, crewId2].sort().join('-');
  }

  getRelationship(crewId1, crewId2) {
    const key = this.getRelationshipKey(crewId1, crewId2);
    return this.relationships.get(key) || {
      score: 50,
      status: RELATIONSHIP_STATUS.NEUTRAL,
      history: []
    };
  }

  updateRelationship(crewId1, crewId2, scoreChange, interaction) {
    const key = this.getRelationshipKey(crewId1, crewId2);
    const rel = this.getRelationship(crewId1, crewId2);
    
    const oldScore = rel.score;
    const oldStatus = rel.status;
    
    rel.score = Math.max(0, Math.min(100, rel.score + scoreChange));
    rel.status = this.getStatusFromScore(rel.score);
    
    // Check for milestone (status change)
    if (oldStatus !== rel.status) {
      const crew1 = CrewManager?.getCrew(crewId1);
      const crew2 = CrewManager?.getCrew(crewId2);
      
      if (crew1 && crew2) {
        const improved = rel.score > oldScore;
        const emoji = improved ? '🎉' : '😟';
        EventBus.emit(EVENT_TYPES.CREW_RELATIONSHIP_MILESTONE, {
          crew1: crew1.name,
          crew2: crew2.name,
          oldStatus,
          newStatus: rel.status,
          improved,
          message: `${emoji} ${crew1.name} and ${crew2.name} are now ${rel.status}!`
        });
      }
    }
    
    rel.history.push({
      timestamp: Date.now(),
      interaction,
      scoreChange
    });

    if (rel.history.length > 50) {
      rel.history = rel.history.slice(-50);
    }

    this.relationships.set(key, rel);
    this.save();
  }

  getStatusFromScore(score) {
    if (score >= 80) return RELATIONSHIP_STATUS.CLOSE;
    if (score >= 60) return RELATIONSHIP_STATUS.FRIENDLY;
    if (score >= 40) return RELATIONSHIP_STATUS.NEUTRAL;
    if (score >= 20) return RELATIONSHIP_STATUS.DISTANT;
    return RELATIONSHIP_STATUS.HOSTILE;
  }

  getAllRelationships(crewId) {
    const relationships = {};
    for (const [key, rel] of this.relationships) {
      const [id1, id2] = key.split('-');
      if (id1 === crewId) relationships[id2] = rel;
      else if (id2 === crewId) relationships[id1] = rel;
    }
    return relationships;
  }

  adjustRelationship(crewId1, crewId2, change, reason = 'activity') {
    this.updateRelationship(crewId1, crewId2, change, reason);
  }

  /**
   * Mediate a conflict between two crew members
   * Works best on low relationships (< 40)
   * @returns {Object} { success: boolean, improvement: number, message: string }
   */
  mediateConflict(crewId1, crewId2) {
    const rel = this.getRelationship(crewId1, crewId2);
    const currentScore = rel.score;
    
    // Success chance: 70% base, +10% if relationship is very low
    const successChance = currentScore < 30 ? config.mediation.successChance + config.mediation.lowRelationshipBonus : config.mediation.successChance;
    const success = Math.random() < successChance;
    
    if (success) {
      const improvement = Math.floor(Math.random() * (config.mediation.successMax - config.mediation.successMin + 1)) + config.mediation.successMin; // 10-20
      this.updateRelationship(crewId1, crewId2, improvement, 'mediation');
      return {
        success: true,
        improvement,
        message: `Mediation successful! Relationship improved by ${improvement} points.`
      };
    } else {
      const improvement = Math.floor(Math.random() * (config.mediation.partialMax - config.mediation.partialMin + 1)) + config.mediation.partialMin; // 2-7
      this.updateRelationship(crewId1, crewId2, improvement, 'mediation_partial');
      return {
        success: false,
        improvement,
        message: `Mediation partially successful. Relationship improved by ${improvement} points.`
      };
    }
  }

  /**
   * Conduct a counseling session for two crew members
   * More effective than mediation, works at any relationship level
   * @returns {Object} { success: boolean, improvement: number, message: string }
   */
  counselSession(crewId1, crewId2) {
    const improvement = Math.floor(Math.random() * (config.counseling.improvementMax - config.counseling.improvementMin + 1)) + config.counseling.improvementMin; // 15-25
    this.updateRelationship(crewId1, crewId2, improvement, 'counseling');
    
    return {
      success: true,
      improvement,
      message: `Counseling session successful! Relationship improved by ${improvement} points.`
    };
  }

  /**
   * Organize a team building activity for all crew
   * Small boost to all relationships
   * @param {Array} crewIds - Array of all crew member IDs
   * @returns {Object} { totalImproved: number, avgImprovement: number, message: string }
   */
  teamBuildingActivity(crewIds) {
    let totalImproved = 0;
    let relationshipCount = 0;
    
    // Improve all crew relationships
    for (let i = 0; i < crewIds.length; i++) {
      for (let j = i + 1; j < crewIds.length; j++) {
        const improvement = Math.floor(Math.random() * (config.teamBuilding.improvementMax - config.teamBuilding.improvementMin + 1)) + config.teamBuilding.improvementMin; // 5-10
        this.updateRelationship(crewIds[i], crewIds[j], improvement, 'team_building');
        totalImproved += improvement;
        relationshipCount++;
      }
    }
    
    const avgImprovement = relationshipCount > 0 ? Math.round(totalImproved / relationshipCount) : 0;
    
    return {
      totalImproved,
      avgImprovement,
      relationshipCount,
      message: `Team building successful! ${relationshipCount} relationships improved by an average of ${avgImprovement} points.`
    };
  }

  /**
   * Calculate combat bonus based on crew relationships
   * Good relationships improve combat effectiveness
   * @param {Array} crewIds - Array of crew member IDs
   * @returns {number} Damage multiplier (1.0 = no bonus, 1.2 = +20%)
   */
  getCombatBonus(crewIds) {
    if (crewIds.length < 2) return 1.0;
    
    let totalScore = 0;
    let relationshipCount = 0;
    
    for (let i = 0; i < crewIds.length; i++) {
      for (let j = i + 1; j < crewIds.length; j++) {
        const rel = this.getRelationship(crewIds[i], crewIds[j]);
        totalScore += rel.score;
        relationshipCount++;
      }
    }
    
    const avgScore = relationshipCount > 0 ? totalScore / relationshipCount : 50;
    
    // Convert average score to bonus
    // 0-20: 0.8x (penalty)
    // 20-40: 0.9x (small penalty)
    // 40-60: 1.0x (neutral)
    // 60-80: 1.1x (+10%)
    // 80-100: 1.2x (+20%)
    if (avgScore >= config.thresholds.friendly) return config.combatBonus.excellent;
    if (avgScore >= config.thresholds.neutral) return config.combatBonus.good;
    if (avgScore >= config.thresholds.distant) return config.combatBonus.neutral;
    if (avgScore >= config.thresholds.hostile) return config.combatBonus.poor;
    return config.combatBonus.critical;
  }

  /**
   * Calculate efficiency bonus for ship operations
   * Good relationships improve work efficiency
   * @param {Array} crewIds - Array of crew member IDs
   * @returns {number} Efficiency multiplier (1.0 = no bonus, 1.15 = +15%)
   */
  getEfficiencyBonus(crewIds) {
    if (crewIds.length < 2) return 1.0;
    
    let totalScore = 0;
    let relationshipCount = 0;
    
    for (let i = 0; i < crewIds.length; i++) {
      for (let j = i + 1; j < crewIds.length; j++) {
        const rel = this.getRelationship(crewIds[i], crewIds[j]);
        totalScore += rel.score;
        relationshipCount++;
      }
    }
    
    const avgScore = relationshipCount > 0 ? totalScore / relationshipCount : 50;
    
    // Convert average score to efficiency bonus
    // 0-20: 0.85x (penalty)
    // 20-40: 0.95x (small penalty)
    // 40-60: 1.0x (neutral)
    // 60-80: 1.05x (+5%)
    // 80-100: 1.15x (+15%)
    if (avgScore >= config.thresholds.friendly) return config.efficiencyBonus.excellent;
    if (avgScore >= config.thresholds.neutral) return config.efficiencyBonus.good;
    if (avgScore >= config.thresholds.distant) return config.combatBonus.neutral;
    if (avgScore >= config.thresholds.hostile) return config.efficiencyBonus.poor;
    return config.efficiencyBonus.critical;
  }

  /**
   * Get team dynamics summary
   * @param {Array} crewIds - Array of crew member IDs
   * @returns {Object} Summary of team dynamics
   */
  getTeamDynamics(crewIds) {
    if (crewIds.length < 2) {
      return {
        avgScore: 50,
        status: 'NEUTRAL',
        combatBonus: 1.0,
        efficiencyBonus: 1.0,
        conflicts: 0,
        friendships: 0
      };
    }
    
    let totalScore = 0;
    let relationshipCount = 0;
    let conflicts = 0;
    let friendships = 0;
    
    for (let i = 0; i < crewIds.length; i++) {
      for (let j = i + 1; j < crewIds.length; j++) {
        const rel = this.getRelationship(crewIds[i], crewIds[j]);
        totalScore += rel.score;
        relationshipCount++;
        
        if (rel.score < 40) conflicts++;
        if (rel.score >= 60) friendships++;
      }
    }
    
    const avgScore = Math.round(totalScore / relationshipCount);
    const combatBonus = this.getCombatBonus(crewIds);
    const efficiencyBonus = this.getEfficiencyBonus(crewIds);
    
    let status = 'NEUTRAL';
    if (avgScore >= 80) status = 'EXCELLENT';
    else if (avgScore >= 60) status = 'GOOD';
    else if (avgScore >= 40) status = 'NEUTRAL';
    else if (avgScore >= 20) status = 'POOR';
    else status = 'CRITICAL';
    
    return {
      avgScore,
      status,
      combatBonus,
      efficiencyBonus,
      conflicts,
      friendships,
      relationshipCount
    };
  }

  /**
   * Apply mission success impact to relationships
   * Success improves relationships, failure strains them
   * @param {Array} crewIds - Crew members on the mission
   * @param {boolean} success - Whether mission succeeded
   * @returns {Object} Impact summary
   */
  applyMissionImpact(crewIds, success) {
    if (crewIds.length < 2) return { affected: 0, avgChange: 0 };
    
    let totalChange = 0;
    let affected = 0;
    
    for (let i = 0; i < crewIds.length; i++) {
      for (let j = i + 1; j < crewIds.length; j++) {
        const change = success ? Math.floor(Math.random() * (config.teamBuilding.improvementMax - config.teamBuilding.improvementMin + 1)) + config.teamBuilding.improvementMin : -(Math.floor(Math.random() * 4) + 2);
        this.updateRelationship(crewIds[i], crewIds[j], change, success ? 'mission_success' : 'mission_failure');
        totalChange += change;
        affected++;
      }
    }
    
    const avgChange = Math.round(totalChange / affected);
    
    // Emit event for UI notification
    EventBus.emit(EVENT_TYPES.CREW_RELATIONSHIP_CHANGE, {
      type: success ? 'mission_success' : 'mission_failure',
      avgChange,
      affected,
      message: success 
        ? `💪 Combat victory strengthened team bonds! (+${avgChange} avg)`
        : `😔 Combat defeat strained relationships (${avgChange} avg)`
    });
    
    return {
      affected,
      avgChange,
      message: success 
        ? `Mission success! Team bonds strengthened (+${avgChange} avg).`
        : `Mission failure strained relationships (${avgChange} avg).`
    };
  }

  /**
   * Check for personality conflicts and apply them
   * @param {Object} crew1 - First crew member with personality
   * @param {Object} crew2 - Second crew member with personality
   * @returns {Object} Conflict result
   */
  checkPersonalityConflict(crew1, crew2) {
    if (!crew1.personality || !crew2.personality) return { conflict: false };
    
    const p1 = crew1.personality;
    const p2 = crew2.personality;
    
    // Check for major conflicts
    const conflicts = [];
    
    // Introvert vs Extrovert extreme conflict
    if (Math.abs(p1.introvert - p2.introvert) > 70) {
      conflicts.push('social_style');
    }
    
    // Serious vs Humorous conflict
    if (Math.abs(p1.serious - p2.serious) > 70) {
      conflicts.push('attitude');
    }
    
    // Cautious vs Bold conflict
    if (Math.abs(p1.cautious - p2.cautious) > 70) {
      conflicts.push('risk_tolerance');
    }
    
    if (conflicts.length > 0) {
      const change = -(conflicts.length * config.personalityConflict.penaltyPerType);
      this.updateRelationship(crew1.id, crew2.id, change, 'personality_conflict');
      
      // Emit event for UI notification
      EventBus.emit(EVENT_TYPES.CREW_RELATIONSHIP_CHANGE, {
        type: 'personality_conflict',
        crew1: crew1.name,
        crew2: crew2.name,
        change,
        conflicts: conflicts.join(', '),
        message: `⚠️ ${crew1.name} and ${crew2.name} have personality conflicts (${change})`
      });
      
      return {
        conflict: true,
        types: conflicts,
        change,
        message: `${crew1.name} and ${crew2.name} had a personality clash (${change}).`
      };
    }
    
    return { conflict: false };
  }

  /**
   * Apply time decay to relationships
   * Relationships slowly drift toward neutral (50) over time
   * @param {Array} crewIds - All crew member IDs
   * @returns {Object} Decay summary
   */
  applyTimeDecay(crewIds) {
    if (crewIds.length < 2) return { affected: 0 };
    
    let affected = 0;
    
    for (let i = 0; i < crewIds.length; i++) {
      for (let j = i + 1; j < crewIds.length; j++) {
        const rel = this.getRelationship(crewIds[i], crewIds[j]);
        
        // Drift toward 50 (neutral)
        if (rel.score > 50) {
          this.updateRelationship(crewIds[i], crewIds[j], -1, 'time_decay');
          affected++;
        } else if (rel.score < 50) {
          this.updateRelationship(crewIds[i], crewIds[j], 1, 'time_decay');
          affected++;
        }
      }
    }
    
    return { affected };
  }

  /**
   * Trigger random relationship event
   * @param {Array} crew - All crew members
   * @returns {Object} Event result
   */
  triggerRandomEvent(crew) {
    if (crew.length < 2) return { occurred: false };
    
    // 20% chance of event
    if (Math.random() > 0.2) return { occurred: false };
    
    // Pick two random crew members
    const crew1 = crew[Math.floor(Math.random() * crew.length)];
    const crew2 = crew[Math.floor(Math.random() * crew.length)];
    
    if (crew1.id === crew2.id) return { occurred: false };
    
    const events = [
      { type: 'shared_meal', change: 5, message: 'shared a meal together' },
      { type: 'argument', change: -8, message: 'had an argument' },
      { type: 'helped', change: 10, message: 'helped each other out' },
      { type: 'disagreement', change: -5, message: 'had a disagreement' },
      { type: 'bonding', change: 12, message: 'bonded over shared interests' },
      { type: 'misunderstanding', change: -6, message: 'had a misunderstanding' }
    ];
    
    const event = events[Math.floor(Math.random() * events.length)];
    this.updateRelationship(crew1.id, crew2.id, event.change, event.type);
    
    return {
      occurred: true,
      crew1: crew1.name,
      crew2: crew2.name,
      event: event.message,
      change: event.change,
      message: `${crew1.name} and ${crew2.name} ${event.message} (${event.change > 0 ? '+' : ''}${event.change}).`
    };
  }

  save() {
    const data = Object.fromEntries(this.relationships);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  load() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        this.relationships = new Map(Object.entries(parsed));
      }
    } catch (error) {
      console.warn('Failed to load crew relationships:', error);
      this.relationships = new Map();
    }
  }

  reset() {
    this.relationships.clear();
    localStorage.removeItem(STORAGE_KEY);
  }
}

export { RELATIONSHIP_STATUS };
const crewRelationshipManager = new CrewRelationshipManager();
export default crewRelationshipManager;